/**
 * TransactionPicker — the payment behind a record, picked in the row that records it.
 *
 * THIS IS THE SHEET-INSIDE-A-SHEET, RETIRED FROM THE SETUP PAGE. `TransactionSelector` opens a
 * 500px right-hand Sheet over whatever Sheet was already holding the form, draws every candidate
 * as a 96px card, and offers a THIRD Sheet on top of that to record a payment the feed has not
 * caught up with. Five rows on this canvas used it — registration, roadworthiness, service,
 * premium, discount — and every one of them is a single foreign key.
 *
 * IT STAYS MOUNTED IN SEVENTEEN OTHER PLACES. This replaces it on the setup page only; a global
 * swap is a different project, and the two must not diverge in what they WRITE — which is why the
 * create panel here inserts exactly the row the selector's own create sheet inserts, into the same
 * table, and invalidates the same `['unassigned-transactions']` prefix every other writer clears.
 *
 * WHY IT IS ONE COMPONENT AND NOT FIVE COPIES: the five rows differ only in their label and in
 * whether they want a DEBIT or a CREDIT. A copy each would be five places for the create panel's
 * refusal to drift, and the whole point of the queue consequence — an unlinked transaction stays
 * in Unassigned forever — is that every row says it the same way.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';

import {
    createTransaction,
    fetchTransaction,
    fetchUnassignedTransactions,
    transactionKey,
    UNASSIGNED_TRANSACTIONS_KEY,
    unassignedTransactionsKey,
} from '../data/transactions';
import {
    transactionDate,
    transactionDescription,
    transactionLabel,
    transactionSearchText,
    transactionTriggerText,
    validateNewTransaction,
    withSelectedTransaction,
    type PickableTransaction,
} from '../data/transactionPick';
import InlinePickerPopover, {
    PickerPanelActions,
    PickerPanelField,
    PickerPanelNote,
    type PickerRow,
} from './InlinePickerPopover';
import MoneyRow from './MoneyRow';

interface NewTransaction {
    amount: string;
    dated: string;
    description: string;
}

/** The whole payment as one line, for the row's own tooltip. */
function transactionRowTitle(transaction: PickableTransaction): string {
    return [
        transactionLabel(transaction),
        transactionDate(transaction) || 'no date',
        transactionDescription(transaction),
    ].join(' · ');
}

/** Does the text an admin typed into the search box read as an amount, or as a description? */
const LOOKS_LIKE_MONEY = /^[\d.,]+$/;

/**
 * The create panel, seeded ONCE from whatever failed to match.
 *
 * IT OWNS ITS OWN STATE, and that is the point of it being a component rather than three
 * `useState`s in the picker: the render prop runs on every keystroke of the SEARCH box too, so a
 * value derived from `typed` inline would keep reappearing after it was deleted. Mounting seeds
 * it; after that the fields are the admin's.
 */
function NewTransactionPanel({
    id,
    typed,
    pending,
    refusal,
    onCreate,
    onCancel,
}: {
    id: string;
    typed: string;
    pending: boolean;
    refusal: string | null;
    onCreate: (draft: NewTransaction) => void;
    onCancel: () => void;
}) {
    const money = LOOKS_LIKE_MONEY.test(typed);
    const [amount, setAmount] = useState(money ? typed : '');
    const [dated, setDated] = useState('');
    const [description, setDescription] = useState(money ? '' : typed);

    const invalid = validateNewTransaction({ amount, dated });

    return (
        <>
            <PickerPanelField id={`${id}-new-amount`} label="Amount (GHS)">
                <input
                    id={`${id}-new-amount`}
                    inputMode="decimal"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    placeholder="0.00"
                />
            </PickerPanelField>
            <PickerPanelField id={`${id}-new-date`} label="Date the money moved">
                <input
                    id={`${id}-new-date`}
                    type="date"
                    value={dated}
                    onChange={(event) => setDated(event.target.value)}
                />
            </PickerPanelField>
            <PickerPanelField id={`${id}-new-desc`} label="Description">
                <input
                    id={`${id}-new-desc`}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="What the bank called it"
                />
            </PickerPanelField>
            {refusal && <PickerPanelNote bad>{refusal}</PickerPanelNote>}
            <PickerPanelActions>
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    Back
                </Button>
                <Button
                    size="sm"
                    disabled={!!invalid || pending}
                    onClick={() => onCreate({ amount, dated, description })}
                >
                    Record it
                </Button>
            </PickerPanelActions>
        </>
    );
}

interface Props {
    /** The DOM id a manifest row's `jump.focus` names — it lands on the trigger. */
    id: string;
    label: string;
    /** Which side of the ledger this row is about. */
    type: 'CREDIT' | 'DEBIT';
    /** `transactions.id` as a string — '' when nothing is linked. */
    value: string;
    /**
     * The id that was linked, AND THE ROW IT CAME OFF, for a host that holds a companion figure.
     *
     * THE AMOUNT TRAVELS WITH THE ID BECAUSE THE DECISION IS MADE BEFORE THE ROUND TRIP. The
     * insurance dossier judges whether the driver's payment covers the premium on the DRAFT — the
     * deposit the admin has just picked, not the one on file — so it needs the picked row's amount
     * at the moment of the click; a read of the server's copy is a round trip behind it, and
     * "covers the premium" decides which instrument the year is published on [D-B45].
     *
     * THE SIGN IS THE CALLER'S BUSINESS. `transactions.amount` carries the ledger's own sign and
     * this picker normalises nothing, because the row is also what a DEBIT host reads. The
     * insurance dossier keeps it — the server judges cover on a raw `SUM(t.amount)` over the same
     * rows (20260902010000:861), so a host that took the sign off would let a credit stored the
     * wrong way round read as money towards a premium the server does not agree was paid.
     *
     * The second argument is OPTIONAL, so every caller that wants only the id keeps its one-line
     * handler and compiles unchanged.
     */
    onChange: (transactionId: string, transaction?: PickableTransaction | null) => void;
    /**
     * Drawn only where the link is genuinely optional — see the roadworthiness row.
     *
     * IT IS THE ONLY DOOR THAT UNLINKS, AND NO `onChange` FOLLOWS IT. A host carrying a figure
     * beside the link clears BOTH here: the amount above outlives its transaction otherwise, and a
     * deposit amount with no id behind it is money that never reaches the database while still
     * telling this screen the premium is covered.
     */
    onClear?: () => void;
    dirty?: boolean;
    error?: string | null;
    disabled?: boolean;
    hint?: ReactNode;
    /** What the trigger reads while nothing is linked. */
    emptyLabel?: string;
}

export default function TransactionPicker({
    id,
    label,
    type,
    value,
    onChange,
    onClear,
    dirty,
    error,
    disabled,
    hint,
    emptyLabel = 'Not linked',
}: Props) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [refusal, setRefusal] = useState<string | null>(null);

    const selectedId = value.trim() === '' ? null : Number(value);

    const { data: pool, isLoading: poolLoading } = useQuery({
        queryKey: unassignedTransactionsKey(search, type),
        queryFn: () => fetchUnassignedTransactions(search, type),
        staleTime: 30000,
    });

    // A LINKED TRANSACTION IS NOT UNASSIGNED, so the pool can never contain the row this trigger
    // is showing. Without this read the field would go blank the moment it was saved.
    const { data: current, isLoading: currentLoading } = useQuery({
        queryKey: transactionKey(selectedId),
        queryFn: () => fetchTransaction(selectedId),
        enabled: selectedId !== null,
        staleTime: 60000,
    });

    const create = useMutation({
        mutationFn: (draft: NewTransaction) => createTransaction({ ...draft, type }),
        onSuccess: (transaction) => {
            queryClient.invalidateQueries({ queryKey: UNASSIGNED_TRANSACTIONS_KEY });
            // Seeded rather than fetched: the row is already in hand, and the trigger has to be
            // able to name it before the pool comes back.
            queryClient.setQueryData(transactionKey(transaction.id), transaction);
            // THE ROW RIDES THE ID HERE TOO, and this is the call site where it matters most: a
            // payment recorded inside the picker is in no pool and no cache the host can read, so
            // a host that went looking for its amount would find nothing at all.
            onChange(String(transaction.id), transaction);
            setRefusal(null);
        },
        // VERBATIM, IN THE PANEL. A refusal that becomes a toast leaves the panel looking correct.
        onError: (raised: Error) => setRefusal(raised.message || 'The transaction was refused.'),
    });

    const rows: PickerRow<PickableTransaction>[] = withSelectedTransaction(pool, current).map(
        (transaction) => ({
            id: String(transaction.id),
            search: transactionSearchText(transaction),
            item: transaction,
        }),
    );

    const selected = rows.find((row) => row.id === value)?.item ?? current ?? null;

    return (
        <InlinePickerPopover<PickableTransaction>
            id={id}
            label={label}
            rows={rows}
            selectedId={selectedId === null ? null : String(selectedId)}
            selectedLabel={transactionTriggerText(selected) ?? emptyLabel}
            emptyLabel={emptyLabel}
            searchPlaceholder="Search by amount, date or description"
            onQueryChange={setSearch}
            noun="payment"
            // THE WHOLE LINE IS THE LABEL, not a label and a meta: the amount is what is being
            // recognised, so it keeps a column of its own rather than being squeezed by whatever
            // the bank wrote. See `MoneyRow`.
            renderLabel={(transaction) => (
                <MoneyRow
                    amount={transactionLabel(transaction)}
                    dated={transactionDate(transaction) || 'no date'}
                    meta={transactionDescription(transaction)}
                />
            )}
            rowTitle={transactionRowTitle}
            // 600, NOT 320: three columns of a payment do not fit in a name's width, and at 500
            // the description — the cell that gives way — was left 240px against an amount and a
            // date holding 208px, so the bank's own text was the MINORITY of a row it is meant to
            // identify. 600 puts 328px under it. See the `width` prop for the arithmetic.
            width={600}
            onSelect={(row) => onChange(row.id, row.item)}
            onClear={onClear}
            clearLabel="No payment linked"
            loading={poolLoading || (selectedId !== null && currentLoading)}
            disabled={disabled}
            dirty={dirty}
            error={error}
            hint={hint}
            create={{
                noun: 'payment',
                requirement:
                    'Only for money that is not in the bank feed yet — everything unassigned is ' +
                    'already in the list above.',
                render: ({ typed, close, cancel }) => (
                    <NewTransactionPanel
                        id={id}
                        typed={typed}
                        pending={create.isPending}
                        refusal={refusal}
                        onCancel={cancel}
                        onCreate={(draft) => create.mutate(draft, { onSuccess: () => close() })}
                    />
                ),
            }}
        />
    );
}
